import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { BlueprintMap } from "./components/BlueprintMap";
import { ProgressCharts } from "./components/Charts";
import { fetchMe, fetchStatus, logout, type UserSession } from "./lib/api";
import { t } from "./lib/i18n";
import {
  type ColorMode,
  type Locale,
  type SiteState,
  type SyncState,
  siteState,
  type VisualSuite,
} from "./shared/project-state";

const visualSuites: VisualSuite[] = ["neural", "forge", "atlas"];
const colorModes: ColorMode[] = ["light", "dark", "parchment"];
const locales: Locale[] = ["zh-CN", "en-US"];

function formatDate(value: string, locale: Locale) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
}

function syncLabel(syncState: SyncState, locale: Locale) {
  if (syncState === "live") return t(locale, "live");
  if (syncState === "stale") return t(locale, "stale");
  if (syncState === "demo") return t(locale, "demo");
  return t(locale, "fallback");
}

export default function App() {
  const [locale, setLocale] = useState<Locale>("zh-CN");
  const [visualSuite, setVisualSuite] = useState<VisualSuite>("neural");
  const [colorMode, setColorMode] = useState<ColorMode>("light");
  const [navDocked, setNavDocked] = useState(false);
  const [state, setState] = useState<SiteState>(siteState);
  const [syncState, setSyncState] = useState<SyncState>("fallback");
  const [fetchedAt, setFetchedAt] = useState(new Date().toISOString());
  const [sourceGeneratedAt, setSourceGeneratedAt] = useState(siteState.generatedAt);
  const [sourceAgeSeconds, setSourceAgeSeconds] = useState(0);
  const [session, setSession] = useState<UserSession>({ authenticated: false });

  const loadStatus = useCallback(async (signal?: AbortSignal) => {
    const payload = await fetchStatus(signal);
    setState(payload.state);
    setSyncState(payload.syncState);
    setFetchedAt(payload.fetchedAt);
    setSourceGeneratedAt(payload.sourceGeneratedAt);
    setSourceAgeSeconds(payload.sourceAgeSeconds);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dataset.visualSuite = visualSuite;
    document.documentElement.dataset.colorMode = colorMode;
  }, [colorMode, locale, visualSuite]);

  useEffect(() => {
    let frame = 0;
    const updateDockState = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setNavDocked(window.scrollY > 96);
      });
    };
    updateDockState();
    window.addEventListener("scroll", updateDockState, { passive: true });
    window.addEventListener("resize", updateDockState);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateDockState);
      window.removeEventListener("resize", updateDockState);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadStatus(controller.signal);
    void fetchMe(controller.signal).then(setSession);
    const timer = window.setInterval(() => {
      void loadStatus();
    }, 30000);
    let events: EventSource | undefined;
    try {
      events = new EventSource("/api/events");
      events.addEventListener("status", (event) => {
        const payload = JSON.parse((event as MessageEvent).data) as {
          state: SiteState;
          syncState: SyncState;
          fetchedAt: string;
          sourceGeneratedAt: string;
          sourceAgeSeconds: number;
        };
        setState(payload.state);
        setSyncState(payload.syncState);
        setFetchedAt(payload.fetchedAt);
        setSourceGeneratedAt(payload.sourceGeneratedAt);
        setSourceAgeSeconds(payload.sourceAgeSeconds);
      });
    } catch {
      events = undefined;
    }
    return () => {
      controller.abort();
      window.clearInterval(timer);
      events?.close();
    };
  }, [loadStatus]);

  const taskTotals = useMemo(
    () => ({
      total: state.stages.reduce((sum, stage) => sum + stage.taskTotal, 0),
      done: state.stages.reduce((sum, stage) => sum + stage.taskDone, 0),
    }),
    [state],
  );
  const showcaseCards = [
    {
      tag: locale === "zh-CN" ? "官网" : "Site",
      title: locale === "zh-CN" ? "活蓝图已经上线" : "Living blueprint is live",
      body:
        locale === "zh-CN"
          ? "aios.billow.im 展示 L0→L5、τ 链路、能力胶囊、云镜像与用户决策边界。"
          : "aios.billow.im shows L0→L5, τ chain, capsules, cloud mirror and decision boundaries.",
      proof:
        locale === "zh-CN" ? `${state.overallProgress}% 总体观察态` : `${state.overallProgress}% observed`,
    },
    {
      tag: locale === "zh-CN" ? "桌面" : "Desktop",
      title: locale === "zh-CN" ? "AIOS Home 可本地打开" : "AIOS Home runs locally",
      body:
        locale === "zh-CN"
          ? "Tauri 桌面壳把 Brief、BEA、Runtime Preview、Skill/开源源准入收进同一个创作入口。"
          : "The Tauri shell puts Brief, BEA, Runtime Preview and Skill/source intake into one creation home.",
      proof: locale === "zh-CN" ? "本地优先 · 只读默认" : "local-first · read-only by default",
    },
    {
      tag: locale === "zh-CN" ? "大脑" : "Brain",
      title: locale === "zh-CN" ? "BEA + 本地模型进入建议层" : "BEA + local model advisory",
      body:
        locale === "zh-CN"
          ? "qwen2.5-coder:7b 只给 proposal / quarantine / deny 建议，不能越权执行。"
          : "qwen2.5-coder:7b can propose / quarantine / deny, but it cannot execute by itself.",
      proof: locale === "zh-CN" ? "Advisory-only" : "advisory-only",
    },
    {
      tag: locale === "zh-CN" ? "Runtime" : "Runtime",
      title: locale === "zh-CN" ? "受控动作先预览再批准" : "Actions preview before approval",
      body:
        locale === "zh-CN"
          ? "Preview Lab 显示权限、回滚、Evidence、Reviewer；正式副作用仍被安全门拦住。"
          : "Preview Lab shows permission, rollback, Evidence and Reviewer before any formal side effect.",
      proof: locale === "zh-CN" ? "no_external_write" : "no_external_write",
    },
  ];
  const showcaseRoute = [
    locale === "zh-CN" ? "说出一个产品想法" : "Say a product idea",
    locale === "zh-CN" ? "AIOS 生成 Task Spec 草稿" : "AIOS drafts a Task Spec",
    locale === "zh-CN" ? "BEA/LLM/Skill 给出路线建议" : "BEA/LLM/Skill propose routes",
    locale === "zh-CN" ? "Runtime Preview 显示风险与回滚" : "Runtime Preview shows risks and rollback",
    locale === "zh-CN" ? "你截图反馈，进入下一轮" : "You screenshot feedback and iterate",
  ];
  const showcaseBoundaries = [
    "read-only-by-default",
    "proposal-only",
    "evidence-first",
    "reviewer-before-side-effect",
  ];

  return (
    <main className={navDocked ? "site-shell nav-docked" : "site-shell"}>
      <div className="topbar-frame">
        <header className={navDocked ? "topbar docked" : "topbar"}>
          <a className="brand" href="#top" aria-label="AIOS">
            <img src="/aios.svg" alt="" />
            <span>AIOS</span>
          </a>
          <nav aria-label={locale === "zh-CN" ? "主导航" : "Primary navigation"}>
            <a href="#showcase">{locale === "zh-CN" ? "成果" : "Showcase"}</a>
            <a href="#blueprint">{t(locale, "navBlueprint")}</a>
            <a href="#mission">{t(locale, "navMission")}</a>
            <a href="#tau">{t(locale, "navTau")}</a>
            <a href="#capsules">{t(locale, "navCapsules")}</a>
            <a href="#ecosystem">{locale === "zh-CN" ? "生态" : "Ecosystem"}</a>
            <a href="#aios-home-comprehension">{locale === "zh-CN" ? "一眼懂" : "30s"}</a>
            <a href="#creative-flow-clarity">{locale === "zh-CN" ? "跑道" : "Runway"}</a>
            <a href="#official-site-living-blueprint">{locale === "zh-CN" ? "证明" : "Proof"}</a>
            <a href="#creative-starter">{locale === "zh-CN" ? "创作" : "Create"}</a>
            <a href="#runtime-preview-trust">{locale === "zh-CN" ? "预览" : "Preview"}</a>
            <a href="#handoff">{locale === "zh-CN" ? "交接" : "Handoff"}</a>
            <a href="#local-product-polish-roadmap">{locale === "zh-CN" ? "本地路线" : "Local polish"}</a>
            <a href="#decisions">{locale === "zh-CN" ? "决策" : "Decisions"}</a>
            <a href="#decision-readiness">{locale === "zh-CN" ? "体检" : "Readiness"}</a>
            <a href="#activation">{locale === "zh-CN" ? "激活" : "Activation"}</a>
            <a href="#prompts">{locale === "zh-CN" ? "提示" : "Prompts"}</a>
            <a href="#safety-harness">{locale === "zh-CN" ? "安全锁" : "Safety"}</a>
            <a href="#sources">{t(locale, "navSources")}</a>
            <a href="#features">{t(locale, "navFeatures")}</a>
            <a href="#login">{t(locale, "navLogin")}</a>
          </nav>
        </header>
      </div>

      <section id="top" className="hero">
        <div className="hero-copy">
          <p className="eyebrow">{t(locale, "heroEyebrow")}</p>
          <h1>{t(locale, "heroTitle")}</h1>
          <p>{t(locale, "heroBody")}</p>
          <div className="hero-actions">
            <a className="button primary" href="#showcase">
              {t(locale, "primaryCta")}
            </a>
            <a className="button secondary" href="#creative-starter">
              {locale === "zh-CN" ? "开始创作路径" : "Open creation path"}
            </a>
          </div>
        </div>
        <aside className="hero-panel" aria-label={t(locale, "overall")}>
          <div className={`status-pill ${syncState}`}>{syncLabel(syncState, locale)}</div>
          <strong>{state.overallProgress}%</strong>
          <span>{t(locale, "overall")}</span>
          <small>
            {t(locale, "lastUpdated")} · {formatDate(fetchedAt, locale)}
          </small>
          <small>
            {t(locale, "sourceSnapshot")} · {formatDate(sourceGeneratedAt, locale)} · {t(locale, "sourceAge")}{" "}
            {Math.floor(sourceAgeSeconds / 60)}m
          </small>
          <small className="authority-note">{t(locale, "notFormalAuthority")}</small>
        </aside>
      </section>

      <section id="showcase" className="panel-section showcase-section" aria-labelledby="showcase-title">
        <div className="section-heading showcase-heading">
          <p className="eyebrow">
            {locale === "zh-CN" ? "AIOS Showcase · 所见即所得" : "AIOS Showcase · visible outcomes"}
          </p>
          <h2 id="showcase-title">
            {locale === "zh-CN"
              ? "这不是一张进度表：这是你今天能打开、能理解、能继续创作的 AIOS。"
              : "This is not a progress table: it is the AIOS you can open, understand and keep creating with today."}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "官网负责把蓝图讲清楚，桌面端负责把创作闭环放到你手里；BEA、本地模型、Skill、开源源和 Runtime 预览都在同一条受控创作跑道上。"
              : "The site explains the blueprint; the desktop puts the creation loop in your hands. BEA, local models, Skills, open-source sources and Runtime Preview share one governed runway."}
          </p>
        </div>
        <div className="showcase-grid">
          {showcaseCards.map((card) => (
            <article className="showcase-card" key={card.title}>
              <span>{card.tag}</span>
              <strong>{card.title}</strong>
              <p>{card.body}</p>
              <small>{card.proof}</small>
            </article>
          ))}
        </div>
        <div
          className="showcase-runway"
          role="group"
          aria-label={locale === "zh-CN" ? "体验路线" : "Experience runway"}
        >
          <div>
            <span>{locale === "zh-CN" ? "5 分钟体验" : "5-minute path"}</span>
            <strong>
              {locale === "zh-CN" ? "从一个想法走到受控候选" : "From one idea to a governed candidate"}
            </strong>
          </div>
          <ol>
            {showcaseRoute.map((step, index) => (
              <li key={step}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{step}</strong>
              </li>
            ))}
          </ol>
        </div>
        <div className="showcase-launchpad">
          <article>
            <span>{locale === "zh-CN" ? "打开官网" : "Open site"}</span>
            <strong>https://aios.billow.im</strong>
            <p>{locale === "zh-CN" ? "公开展示层，默认只读。" : "Public showcase, read-only by default."}</p>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "启动桌面" : "Launch desktop"}</span>
            <strong>pnpm devx:tauri-window-preview:run</strong>
            <p>
              {locale === "zh-CN"
                ? "本地 GUI OS 入口；不会自动执行外部动作。"
                : "Local GUI OS entry; it will not auto-execute external actions."}
            </p>
          </article>
          <div className="showcase-boundaries" role="group" aria-label="showcase boundaries">
            {showcaseBoundaries.map((boundary) => (
              <small key={boundary}>{boundary}</small>
            ))}
          </div>
        </div>
      </section>

      <section className="control-strip" aria-label="display controls">
        <ControlGroup label={t(locale, "themeSuite")}>
          {visualSuites.map((suite) => (
            <button
              key={suite}
              type="button"
              aria-pressed={visualSuite === suite}
              onClick={() => setVisualSuite(suite)}
            >
              {t(locale, suite)}
            </button>
          ))}
        </ControlGroup>
        <ControlGroup label={t(locale, "colorMode")}>
          {colorModes.map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={colorMode === mode}
              onClick={() => setColorMode(mode)}
            >
              {t(locale, mode)}
            </button>
          ))}
        </ControlGroup>
        <ControlGroup label={t(locale, "locale")}>
          {locales.map((entry) => (
            <button
              key={entry}
              type="button"
              aria-pressed={locale === entry}
              onClick={() => setLocale(entry)}
            >
              {entry}
            </button>
          ))}
        </ControlGroup>
      </section>

      <section
        id="aios-home-comprehension"
        className="panel-section comprehension-section"
        aria-labelledby="aios-home-comprehension-title"
      >
        <div className="section-heading">
          <p className="eyebrow">
            {locale === "zh-CN" ? "入口导览 · 创作型 GUI OS" : "entry guide · creative GUI OS"}
          </p>
          <h2 id="aios-home-comprehension-title">
            {locale === "zh-CN" ? "30 秒理解 AIOS" : "Understand AIOS in 30 seconds"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 不是项目报表页，也不是 Codex 克隆。它把说出想法、组织 Codex / 模型 / Skill / GitHub / IDE 能力、生成受控任务、预览反馈放进同一条创作操作系统路径。"
              : "AIOS is not a project-report page or a Codex clone. It puts idea capture, Codex / model / Skill / GitHub / IDE routing, governed work drafting, preview and feedback into one creation operating-system path."}
          </p>
        </div>
        <div className="comprehension-metrics">
          <article>
            <span>{locale === "zh-CN" ? "可立即执行" : "executable now"}</span>
            <strong>{state.aiosHomeComprehensionPass.canExecuteNowCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部副作用" : "external effects"}</span>
            <strong>{state.aiosHomeComprehensionPass.externalEffectCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Secret 值" : "secret values"}</span>
            <strong>{state.aiosHomeComprehensionPass.secretValuesEmbeddedCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "用户决策" : "user decisions"}</span>
            <strong>{state.aiosHomeComprehensionPass.remainingUserDecisionCount}</strong>
          </article>
        </div>
        <div className="comprehension-card-grid">
          {state.aiosHomeComprehensionPass.oneGlanceCards.map((card) => (
            <article key={card.id}>
              <span>{card.role}</span>
              <strong>{locale === "zh-CN" ? card.titleZh : card.titleEn}</strong>
              <p>{locale === "zh-CN" ? card.bodyZh : card.bodyEn}</p>
              <small>can_execute_now={String(card.canExecuteNow)}</small>
            </article>
          ))}
        </div>
        <ol className="comprehension-flow">
          {state.aiosHomeComprehensionPass.creativeFlowSteps.map((step, index) => (
            <li key={step.id}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{locale === "zh-CN" ? step.labelZh : step.labelEn}</strong>
              <small>{locale === "zh-CN" ? step.outputZh : step.outputEn}</small>
            </li>
          ))}
        </ol>
        <div className="handoff-readiness-boundary">
          {state.aiosHomeComprehensionPass.guardrails.map((guardrail) => (
            <span key={guardrail}>{guardrail}</span>
          ))}
          <span>{state.aiosHomeComprehensionPass.visibleCapability}</span>
          <span>
            adds_new_visible_capability={String(state.aiosHomeComprehensionPass.addsNewVisibleCapability)}
          </span>
        </div>
      </section>

      <section
        id="creative-flow-clarity"
        className="panel-section creative-flow-clarity-section"
        aria-labelledby="creative-flow-clarity-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Creative runway</p>
          <h2 id="creative-flow-clarity-title">
            {locale === "zh-CN" ? "把创作闭环变成跑道" : "Turn the creative loop into a runway"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 的前台不应该让你先看工程表，而应该让你顺着一条熟悉的路径进入创作：Brief、Task Spec、Intent Queue、能力路线、Preview Lab、反馈回流。当前仍是只读解释层，不能执行或写外部。"
              : "The AIOS front door should not make you start with engineering tables. It should move you through a familiar creation path: Brief, Task Spec, Intent Queue, capability route, Preview Lab and feedback. This remains a read-only explanation layer with no execution or external writes."}
          </p>
        </div>
        <div className="comprehension-metrics">
          <article>
            <span>{locale === "zh-CN" ? "跑道步骤" : "runway steps"}</span>
            <strong>{state.creativeFlowClarity.runwayStepCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "交接桥" : "handoff bridges"}</span>
            <strong>{state.creativeFlowClarity.handoffBridgeCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "可执行" : "executable now"}</span>
            <strong>{state.creativeFlowClarity.canExecuteNowCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部副作用" : "external effects"}</span>
            <strong>{state.creativeFlowClarity.externalEffectCount}</strong>
          </article>
        </div>
        <ol className="creative-runway-grid">
          {state.creativeFlowClarity.runwaySteps.map((step, index) => (
            <li key={step.id}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{locale === "zh-CN" ? step.titleZh : step.titleEn}</strong>
              <p>{locale === "zh-CN" ? step.bodyZh : step.bodyEn}</p>
              <small>
                {locale === "zh-CN" ? "输入" : "input"}: {locale === "zh-CN" ? step.inputZh : step.inputEn}
              </small>
              <small>
                {locale === "zh-CN" ? "输出" : "output"}: {locale === "zh-CN" ? step.outputZh : step.outputEn}
              </small>
              <small>
                {step.authorityState} · can_execute_now={String(step.canExecuteNow)}
              </small>
            </li>
          ))}
        </ol>
        <div className="creative-runway-bridges">
          {state.creativeFlowClarity.handoffBridges.map((bridge) => (
            <span key={`${bridge.from}-${bridge.to}`}>
              {bridge.from} → {bridge.to} · {bridge.rule}
            </span>
          ))}
        </div>
        <div className="handoff-readiness-boundary">
          {state.creativeFlowClarity.guardrails.map((guardrail) => (
            <span key={guardrail}>{guardrail}</span>
          ))}
          <span>{state.creativeFlowClarity.visibleCapability}</span>
          <span>
            adds_new_visible_capability={String(state.creativeFlowClarity.addsNewVisibleCapability)}
          </span>
        </div>
      </section>

      <section
        id="official-site-living-blueprint"
        className="panel-section living-blueprint-proof-section"
        aria-labelledby="official-site-living-blueprint-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Living Blueprint proof</p>
          <h2 id="official-site-living-blueprint-title">
            {locale === "zh-CN"
              ? "官网不是宣传页，而是活蓝图"
              : "The site is a living blueprint, not a brochure"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "这个官网承担 AIOS 的公开解释职责：说清 AIOS 是什么、当前做到哪、哪些动作还被 Gate 阻断，以及下一次会话应该从哪些证据接上。它仍然只读，不登录、不提交、不部署、不写外部。"
              : "This site carries AIOS' public explanation duty: what AIOS is, where it stands, which actions remain Gate-blocked, and which evidence the next session should resume from. It remains read-only: no login, submit, deploy or external write."}
          </p>
        </div>
        <div className="comprehension-metrics">
          <article>
            <span>{locale === "zh-CN" ? "叙事块" : "narrative blocks"}</span>
            <strong>{state.officialSiteLivingBlueprint.narrativeBlockCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "证明线" : "proof lanes"}</span>
            <strong>{state.officialSiteLivingBlueprint.proofLaneCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部执行" : "external execution"}</span>
            <strong>{state.officialSiteLivingBlueprint.canExecuteExternalCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部副作用" : "external effects"}</span>
            <strong>{state.officialSiteLivingBlueprint.externalEffectCount}</strong>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Secret 值" : "secret values"}</span>
            <strong>{state.officialSiteLivingBlueprint.secretValuesEmbeddedCount}</strong>
          </article>
        </div>
        <div className="living-blueprint-proof-grid">
          {state.officialSiteLivingBlueprint.narrativeBlocks.map((block) => (
            <article key={block.id}>
              <span>{block.proof}</span>
              <strong>{locale === "zh-CN" ? block.titleZh : block.titleEn}</strong>
              <p>{locale === "zh-CN" ? block.bodyZh : block.bodyEn}</p>
            </article>
          ))}
        </div>
        <div className="living-blueprint-proof-lanes">
          {state.officialSiteLivingBlueprint.proofLanes.map((lane) => (
            <span key={lane.id}>
              {locale === "zh-CN" ? lane.labelZh : lane.labelEn} · {lane.source}
            </span>
          ))}
        </div>
        <div className="handoff-readiness-boundary">
          {state.officialSiteLivingBlueprint.guardrails.map((guardrail) => (
            <span key={guardrail}>{guardrail}</span>
          ))}
          <span>{state.officialSiteLivingBlueprint.visibleCapability}</span>
          <span>
            adds_new_visible_capability={String(state.officialSiteLivingBlueprint.addsNewVisibleCapability)}
          </span>
        </div>
      </section>

      <section id="blueprint" className="panel-section">
        <div className="section-heading">
          <p className="eyebrow">L0 → L5</p>
          <h2>{t(locale, "blueprintMap")}</h2>
        </div>
        <BlueprintMap state={state} locale={locale} />
        <div className="stage-grid">
          {state.stages.map((stage) => (
            <article
              key={stage.id}
              className={stage.id === state.currentStage ? "stage-card active" : "stage-card"}
            >
              <span>{stage.id}</span>
              <h3>{locale === "zh-CN" ? stage.nameZh : stage.nameEn}</h3>
              <p>{locale === "zh-CN" ? stage.statusZh : stage.statusEn}</p>
              <small>{locale === "zh-CN" ? stage.nextActionZh : stage.nextActionEn}</small>
              <meter min={0} max={100} value={stage.progress}>
                {stage.progress}%
              </meter>
            </article>
          ))}
        </div>
      </section>

      <section id="tau" className="panel-section">
        <div className="section-heading">
          <p className="eyebrow">τ Law</p>
          <h2>{t(locale, "tauLaw")}</h2>
          <p className="section-copy">{t(locale, "tauBody")}</p>
        </div>
        <div className="tau-chain" role="list" aria-label={t(locale, "tauLaw")}>
          {state.tauChain.map((node, index) => (
            <article key={node.id} className={`tau-node ${node.state}`} role="listitem">
              <span className="tau-index">{index + 1}</span>
              <h3>{locale === "zh-CN" ? node.labelZh : node.labelEn}</h3>
              <strong>{node.tauMinutes}m</strong>
              <p>{locale === "zh-CN" ? node.qualityGateZh : node.qualityGateEn}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="mission" className="mission-grid">
        <div className="mission-card wide">
          <div className="section-heading">
            <p className="eyebrow">{t(locale, "readOnly")}</p>
            <h2>Mission Control</h2>
          </div>
          <div className="metric-row">
            <Metric label={t(locale, "overall")} value={`${state.overallProgress}%`} />
            <Metric label={t(locale, "currentStage")} value={state.currentStage} />
            <Metric label={t(locale, "tasks")} value={`${taskTotals.done}/${taskTotals.total}`} />
            <Metric label={t(locale, "difficulty")} value="Frontier" />
            <Metric label={t(locale, "authorityBoundary")} value="Observational" />
          </div>
          <button type="button" className="button secondary" onClick={() => void loadStatus()}>
            {t(locale, "refresh")}
          </button>
        </div>
        <div className="mission-card">
          <h2>{t(locale, "progressChart")}</h2>
          <ProgressCharts state={state} locale={locale} />
        </div>
        <div className="mission-card">
          <h2>{t(locale, "currentTasks")}</h2>
          <div className="task-list">
            {state.currentTasks.map((task) => (
              <article key={task.id} className={`task-row ${task.state}`}>
                <div>
                  <strong>{task.id}</strong>
                  <p>{locale === "zh-CN" ? task.titleZh : task.titleEn}</p>
                  <small>{locale === "zh-CN" ? task.nextActionZh : task.nextActionEn}</small>
                </div>
                <span>{task.progress}%</span>
              </article>
            ))}
          </div>
        </div>
        <div className="mission-card">
          <h2>{t(locale, "nextTasks")}</h2>
          <div className="task-list">
            {state.nextTasks.map((task) => (
              <article key={task.id} className={`task-row ${task.state}`}>
                <div>
                  <strong>{task.id}</strong>
                  <p>{locale === "zh-CN" ? task.titleZh : task.titleEn}</p>
                  <small>{locale === "zh-CN" ? task.nextActionZh : task.nextActionEn}</small>
                </div>
                <span>{task.progress}%</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="capsules" className="panel-section">
        <div className="section-heading">
          <p className="eyebrow">C0 → C5</p>
          <h2>{t(locale, "capabilityCapsules")}</h2>
          <p className="section-copy">{t(locale, "capsuleBody")}</p>
        </div>
        <div className="capsule-grid">
          {state.capabilityCapsules.map((capsule) => (
            <article key={capsule.id} className="capsule-card">
              <div className="capsule-card__top">
                <span className={`maturity maturity-${capsule.maturity.toLowerCase()}`}>
                  {capsule.maturity}
                </span>
                <small>{capsule.sourceKind}</small>
              </div>
              <h3>{locale === "zh-CN" ? capsule.titleZh : capsule.titleEn}</h3>
              <p>{locale === "zh-CN" ? capsule.nextActionZh : capsule.nextActionEn}</p>
              <dl>
                <div>
                  <dt>default</dt>
                  <dd>{capsule.defaultEnabled ? "on" : "off"}</dd>
                </div>
                <div>
                  <dt>read</dt>
                  <dd>{capsule.readOnlyByDefault ? "read-only" : "scoped"}</dd>
                </div>
                <div>
                  <dt>write</dt>
                  <dd>{capsule.canWrite ? "task-scoped" : "no"}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section id="ecosystem" className="panel-section ecosystem-section" aria-labelledby="ecosystem-title">
        <div className="section-heading">
          <p className="eyebrow">L5 · Ecosystem</p>
          <h2 id="ecosystem-title">{locale === "zh-CN" ? "生态能力雷达" : "Ecosystem radar"}</h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 可以借力插件、Skill、开源源、IDE、浏览器和部署平台；但当前只是只读目录，不安装、不连接、不执行、不写外部系统。"
              : "AIOS can leverage plugins, Skills, open-source sources, IDEs, browsers and deployment platforms; this is currently a read-only catalog with no install, connect, execute or external-write authority."}
          </p>
        </div>
        <div className="ecosystem-dashboard">
          <article className="ecosystem-radar-card">
            <span>{state.ecosystemCatalog.status}</span>
            <strong>{state.ecosystemCatalog.candidateCount}</strong>
            <small>{locale === "zh-CN" ? "候选能力" : "candidate capabilities"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "已启用" : "enabled"}</span>
            <strong>{state.ecosystemCatalog.enabledCount}</strong>
            <small>{locale === "zh-CN" ? "目录不等于接入" : "cataloged is not connected"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "可写/可执行" : "write/execute"}</span>
            <strong>
              {state.ecosystemCatalog.externalWriteNowCount}/{state.ecosystemCatalog.executableNowCount}
            </strong>
            <small>{locale === "zh-CN" ? "全部为 0" : "all zero"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "推荐未安装" : "recommended not installed"}</span>
            <strong>{state.ecosystemCatalog.recommendedNotInstalledCount}</strong>
            <small>{locale === "zh-CN" ? "需要单独批准" : "separate approval required"}</small>
          </article>
        </div>
        <div className="ecosystem-boundary">
          {[
            locale === "zh-CN" ? "安装 0" : "install 0",
            locale === "zh-CN" ? "连接 0" : "connect 0",
            locale === "zh-CN" ? "OAuth 0" : "OAuth 0",
            locale === "zh-CN" ? "执行 0" : "execute 0",
            locale === "zh-CN" ? "外部写入 0" : "external write 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="ecosystem-card-grid">
          {state.ecosystemCatalog.candidates.map((candidate) => (
            <article key={candidate.id} className="ecosystem-card">
              <div>
                <span>{candidate.category}</span>
                <strong>{candidate.name}</strong>
                <small>{candidate.sourceType}</small>
              </div>
              <em>{candidate.maturity}</em>
              <p>{candidate.risk}</p>
              <dl>
                <div>
                  <dt>{locale === "zh-CN" ? "许可" : "license"}</dt>
                  <dd>{candidate.licenseStatus}</dd>
                </div>
                <div>
                  <dt>{locale === "zh-CN" ? "晋级 Gate" : "promotion Gate"}</dt>
                  <dd>{candidate.admissionGate}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
        <div className="ecosystem-gates">
          {state.ecosystemCatalog.futureActivationControls.slice(0, 7).map((control) => (
            <span key={control}>{control.replaceAll("_", " ")}</span>
          ))}
        </div>
      </section>

      <section
        id="sync-readiness"
        className="panel-section sync-readiness-section"
        aria-labelledby="sync-readiness-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · DoR readonly</p>
          <h2 id="sync-readiness-title">
            {locale === "zh-CN" ? "云同步 readiness" : "Cloud-sync readiness"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 已把真实云同步启用前的门槛写成机器可验 DoR：账号、OAuth、Secret、冲突无覆盖、幂等、可观测、回滚与 Reviewer 都未完成前，不能激活。"
              : "AIOS now expresses real cloud-sync activation as a machine-checkable DoR: account, OAuth, Secret, no-clobber conflict handling, idempotency, observability, rollback and Reviewer gates must pass before activation."}
          </p>
        </div>
        <div className="sync-readiness-dashboard">
          <article className="sync-readiness-decision-card">
            <span>{state.syncReadiness.status}</span>
            <strong>{state.syncReadiness.canActivateNow ? "yes" : locale === "zh-CN" ? "否" : "no"}</strong>
            <small>{locale === "zh-CN" ? "当前可激活" : "can activate now"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Readiness Gate" : "Readiness gates"}</span>
            <strong>{state.syncReadiness.readinessGateCount}</strong>
            <small>{locale === "zh-CN" ? "全部阻断" : "all blocked"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "激活阶段" : "activation phases"}</span>
            <strong>{state.syncReadiness.activationPhaseCount}</strong>
            <small>{state.syncReadiness.activationSequence[0]?.name}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "下一安全任务" : "next safe task"}</span>
            <strong>{state.syncReadiness.nextSafeTask}</strong>
            <small>{locale === "zh-CN" ? "只读前台展示" : "readonly UI surface"}</small>
          </article>
        </div>
        <div className="sync-readiness-boundary">
          {[
            locale === "zh-CN" ? "登录 0" : "login 0",
            locale === "zh-CN" ? "OAuth 0" : "OAuth 0",
            locale === "zh-CN" ? "云写入 0" : "cloud write 0",
            locale === "zh-CN" ? "Secret 0" : "Secret 0",
            locale === "zh-CN" ? "Runtime 0" : "Runtime 0",
            locale === "zh-CN" ? "外部写入 0" : "external write 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="sync-readiness-card-grid">
          {state.syncReadiness.readinessGates.map((gate) => (
            <article key={gate.id} className="sync-readiness-card">
              <div>
                <span>{gate.currentState}</span>
                <strong>{locale === "zh-CN" ? gate.titleZh : gate.titleEn}</strong>
              </div>
              <p>{gate.requiredBeforeActivation.slice(0, 2).join(" · ")}</p>
              <small>
                {locale === "zh-CN" ? "禁止捷径" : "forbidden shortcut"}: {gate.forbiddenShortcut}
              </small>
            </article>
          ))}
        </div>
        <div className="sync-readiness-phases">
          {state.syncReadiness.activationSequence.map((phase) => (
            <span key={phase.phase}>
              {phase.phase} · {phase.name} · {phase.status}
            </span>
          ))}
        </div>
      </section>

      <section
        id="publishing-readiness"
        className="panel-section publishing-readiness-section"
        aria-labelledby="publishing-readiness-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Publishing DoR</p>
          <h2 id="publishing-readiness-title">
            {locale === "zh-CN" ? "生态发布 readiness" : "Ecosystem publishing readiness"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 可以沉淀插件、Skill、官网内容与外部分发，但发布不是构建完成的自动结果：许可、安全、质量、费用、账号、回滚、用户明确批准和 Reviewer 都必须先通过。"
              : "AIOS can produce plugins, Skills, official-site content and distribution artifacts, but publishing is not automatic after build: license, safety, quality, cost, account, rollback, explicit user approval and Reviewer gates must pass first."}
          </p>
        </div>
        <div className="publishing-readiness-dashboard">
          <article className="publishing-readiness-decision-card">
            <span>{state.publishingReadiness.status}</span>
            <strong>
              {state.publishingReadiness.canPublishNow ? "yes" : locale === "zh-CN" ? "否" : "no"}
            </strong>
            <small>{locale === "zh-CN" ? "当前可发布" : "can publish now"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "发布 Gate" : "publishing Gates"}</span>
            <strong>{state.publishingReadiness.publishingGateCount}</strong>
            <small>{locale === "zh-CN" ? "全部阻断" : "all blocked"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "发布渠道" : "channels"}</span>
            <strong>{state.publishingReadiness.channelCount}</strong>
            <small>{state.publishingReadiness.channels[0]?.id}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "下一安全任务" : "next safe task"}</span>
            <strong>{state.publishingReadiness.nextSafeTask}</strong>
            <small>{locale === "zh-CN" ? "只读前台展示" : "readonly UI surface"}</small>
          </article>
        </div>
        <div className="publishing-readiness-boundary">
          {[
            locale === "zh-CN" ? "安装 0" : "install 0",
            locale === "zh-CN" ? "市场提交 0" : "marketplace submit 0",
            locale === "zh-CN" ? "外部分发 0" : "external distribution 0",
            locale === "zh-CN" ? "付费动作 0" : "paid action 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="publishing-readiness-card-grid">
          {state.publishingReadiness.publishingGates.map((gate) => (
            <article key={gate.id} className="publishing-readiness-card">
              <div>
                <span>{gate.currentState}</span>
                <strong>{locale === "zh-CN" ? gate.titleZh : gate.titleEn}</strong>
              </div>
              <p>{gate.requiredBeforePublish.slice(0, 2).join(" · ")}</p>
              <small>
                {locale === "zh-CN" ? "禁止捷径" : "forbidden shortcut"}: {gate.forbiddenShortcut}
              </small>
            </article>
          ))}
        </div>
        <div className="publishing-readiness-channels">
          {state.publishingReadiness.channels.map((channel) => (
            <span key={channel.id}>
              {channel.id} · {channel.status}
            </span>
          ))}
        </div>
      </section>

      <section
        id="creative-starter"
        className="panel-section user-decision-center-section"
        aria-labelledby="creative-starter-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Creative starter</p>
          <h2 id="creative-starter-title">
            {locale === "zh-CN" ? "开始创作，而不是看表" : "Start creating, not reading tables"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 的入口应该像 Codex 一样自然：先说想法，再生成 Task Spec，让本地模型只给建议，最后把动作放入受控 Runtime 预览。这里仍然只是只读路径图，没有执行、Runtime、外部写入或 Secret 收集。"
              : "AIOS should start as naturally as Codex: state an idea, draft a Task Spec, let the local model advise only, then place actions into governed Runtime preview. This remains a read-only path map with no execution, Runtime, external write or Secret collection."}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article>
            <span>{locale === "zh-CN" ? "创作路径" : "starter paths"}</span>
            <strong>{state.creativeStarterSurface.starterPathCount}</strong>
            <small>{state.creativeStarterSurface.status}</small>
          </article>
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "可执行" : "executable now"}</span>
            <strong>{state.creativeStarterSurface.canExecuteNowCount}</strong>
            <small>can_execute_now=false</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部副作用" : "external effects"}</span>
            <strong>{state.creativeStarterSurface.externalEffectCount}</strong>
            <small>external_effect_count=0</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Secret 值" : "secret values"}</span>
            <strong>{state.creativeStarterSurface.secretValuesEmbeddedCount}</strong>
            <small>secret_values_embedded_count=0</small>
          </article>
        </div>
        <div className="user-decision-center-boundary">
          {state.creativeStarterSurface.defaultBlockers.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="user-decision-center-card-grid">
          {state.creativeStarterSurface.startPaths.map((startPath) => (
            <article key={startPath.id} className="user-decision-center-card">
              <div>
                <span>
                  {startPath.id} · {startPath.currentAllowedEffect}
                </span>
                <strong>{locale === "zh-CN" ? startPath.titleZh : startPath.titleEn}</strong>
              </div>
              <p>{locale === "zh-CN" ? startPath.intentZh : startPath.intentEn}</p>
              <small>
                {locale === "zh-CN" ? "输入" : "input"}:{" "}
                {locale === "zh-CN" ? startPath.inputZh : startPath.inputEn}
              </small>
              <small>
                {locale === "zh-CN" ? "输出" : "output"}:{" "}
                {locale === "zh-CN" ? startPath.outputZh : startPath.outputEn}
              </small>
              <small>
                Gate: {startPath.gate} · can_execute_now={String(startPath.canExecuteNow)}
              </small>
            </article>
          ))}
        </div>
        <div className="handoff-readiness-channels">
          {(locale === "zh-CN"
            ? state.creativeStarterSurface.loopStepsZh
            : state.creativeStarterSurface.loopStepsEn
          ).map((step, index) => (
            <span key={step}>
              {index + 1}. {step}
            </span>
          ))}
        </div>
      </section>

      <section
        id="runtime-preview-trust"
        className="panel-section runtime-preview-trust-section"
        aria-labelledby="runtime-preview-trust-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Runtime preview trust</p>
          <h2 id="runtime-preview-trust-title">
            {locale === "zh-CN" ? state.runtimePreviewTrust.titleZh : state.runtimePreviewTrust.titleEn}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN" ? state.runtimePreviewTrust.bodyZh : state.runtimePreviewTrust.bodyEn}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article>
            <span>{locale === "zh-CN" ? "信任提示" : "trust cues"}</span>
            <strong>{state.runtimePreviewTrust.trustCueCount}</strong>
            <small>{state.runtimePreviewTrust.status}</small>
          </article>
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "Tauri 调用" : "Tauri invokes"}</span>
            <strong>{state.runtimePreviewTrust.tauriCommandInvocationCount}</strong>
            <small>tauri_command_invocations=0</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "正式写入" : "formal writes"}</span>
            <strong>{state.runtimePreviewTrust.formalTaskWriteCount}</strong>
            <small>formal_task_writes=0</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部副作用" : "external effects"}</span>
            <strong>{state.runtimePreviewTrust.externalEffectCount}</strong>
            <small>external_effect_count=0</small>
          </article>
        </div>
        <div className="runtime-preview-trust-grid">
          {state.runtimePreviewTrust.trustCues.map((cue) => (
            <article key={cue.id}>
              <span>{cue.id}</span>
              <strong>{locale === "zh-CN" ? cue.labelZh : cue.labelEn}</strong>
              <p>{locale === "zh-CN" ? cue.explanationZh : cue.explanationEn}</p>
            </article>
          ))}
        </div>
        <div className="runtime-preview-receipt-row">
          {state.runtimePreviewTrust.receiptSignals.map((signal) => (
            <span key={signal.id}>
              {signal.label}: {signal.value}
            </span>
          ))}
        </div>
        <div className="handoff-readiness-boundary">
          {state.runtimePreviewTrust.guardrails.map((guardrail) => (
            <span key={guardrail}>{guardrail}</span>
          ))}
          <span>{state.runtimePreviewTrust.visibleCapability}</span>
          <span>
            adds_new_visible_capability={String(state.runtimePreviewTrust.addsNewVisibleCapability)}
          </span>
        </div>
      </section>

      <section
        id="handoff"
        className="panel-section handoff-readiness-section"
        aria-labelledby="handoff-readiness-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Final handoff</p>
          <h2 id="handoff-readiness-title">
            {locale === "zh-CN" ? "最终交接 readiness" : "Final handoff readiness"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "AIOS 已把 L5 本地工程面收口到最终交接包：Evidence、可见能力、剩余用户决策和外部动作边界都可见。官网仅展示状态，不触发云同步、生态发布、外部分发、付费、Secret 或 Runtime。"
              : "AIOS has closed the local L5 engineering surface into a final handoff pack: Evidence, visible capabilities, remaining user decisions and external-effect boundaries are visible. The site only observes state; it does not trigger cloud sync, ecosystem publishing, external distribution, paid action, Secret storage or Runtime activation."}
          </p>
        </div>
        <div className="handoff-readiness-dashboard">
          <article className="handoff-readiness-decision-card">
            <span>{locale === "zh-CN" ? "交接状态" : "handoff status"}</span>
            <strong>{state.finalHandoff.status}</strong>
            <small>{state.finalHandoff.document}</small>
          </article>
          <article>
            <span>Evidence</span>
            <strong>{state.finalHandoff.evidenceCount}</strong>
            <small>{locale === "zh-CN" ? "已索引" : "indexed"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "可见能力" : "visible capabilities"}</span>
            <strong>{state.finalHandoff.visibleCapabilityCount}</strong>
            <small>{locale === "zh-CN" ? "桌面与官网" : "desktop and site"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "用户决策" : "user decisions"}</span>
            <strong>{state.finalHandoff.remainingUserDecisionCount}</strong>
            <small>requires_user_decision=true</small>
          </article>
          <article>
            <span>L5</span>
            <strong>{state.finalHandoff.l5Progress}%</strong>
            <small>
              {state.finalHandoff.l5TaskDone}/{state.finalHandoff.l5TaskTotal} ·{" "}
              {locale === "zh-CN" ? "总进度" : "overall"} {state.finalHandoff.overallProgress}%
            </small>
          </article>
        </div>
        <div className="handoff-readiness-boundary">
          {[
            locale === "zh-CN" ? "云同步 0" : "cloud sync 0",
            locale === "zh-CN" ? "生态发布 0" : "ecosystem publish 0",
            locale === "zh-CN" ? "外部分发 0" : "external distribution 0",
            locale === "zh-CN" ? "外部写入 0" : "external write 0",
            locale === "zh-CN" ? "付费 0" : "paid 0",
            "Secret 0",
            "Runtime 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="handoff-readiness-card-grid">
          {state.finalHandoff.remainingUserDecisions.map((decision) => (
            <article key={decision.id} className="handoff-readiness-card">
              <div>
                <span>{decision.state}</span>
                <strong>{decision.id}</strong>
              </div>
              <p>{locale === "zh-CN" ? decision.reasonZh : decision.reasonEn}</p>
              <small>
                requires_user_decision={String(decision.requiresUserDecision)} · can_execute_now=
                {String(decision.canExecuteNow)}
              </small>
            </article>
          ))}
        </div>
        <div className="handoff-readiness-channels">
          {state.finalHandoff.nextSafeEntryPoints.map((entry) => (
            <span key={entry.id}>
              {entry.id} ·{" "}
              {entry.canStartWithoutUserInput
                ? locale === "zh-CN"
                  ? "可继续"
                  : "can continue"
                : locale === "zh-CN"
                  ? "需用户决策"
                  : "needs user decision"}
            </span>
          ))}
        </div>
      </section>

      <section
        id="local-product-polish-roadmap"
        className="panel-section handoff-readiness-section"
        aria-labelledby="local-product-polish-roadmap-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Local polish roadmap</p>
          <h2 id="local-product-polish-roadmap-title">
            {locale === "zh-CN" ? "下一步本地体验路线" : "Next local-product polish route"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "在真实云同步、生态发布和生产级外部分发仍需用户决策前，AIOS 仍可继续安全推进本地 GUI OS、创作闭环、官网 Living Blueprint、Runtime 预览信任感与本地模型建议层。这里是只读路线图：external_effect_count=0、secret_values_embedded_count=0、can_execute_external=false。"
              : "Before real cloud sync, ecosystem publishing and production external distribution receive user decisions, AIOS can still safely polish the local GUI OS, creative loop, Living Blueprint site, Runtime-preview trust and local-model advisory layer. This is a read-only route: external_effect_count=0, secret_values_embedded_count=0, can_execute_external=false."}
          </p>
        </div>
        <div className="handoff-readiness-dashboard">
          <article className="handoff-readiness-decision-card">
            <span>{locale === "zh-CN" ? "路线状态" : "roadmap status"}</span>
            <strong>{state.localProductPolishRoadmap.status}</strong>
            <small>{state.localProductPolishRoadmap.sourceEntryPoint}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "本地任务" : "local items"}</span>
            <strong>{state.localProductPolishRoadmap.roadmapItemCount}</strong>
            <small>{locale === "zh-CN" ? "无需新账号" : "no new account"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "可安全接续" : "safe to continue"}</span>
            <strong>{state.localProductPolishRoadmap.safeWithoutUserInputCount}</strong>
            <small>{locale === "zh-CN" ? "本地 UI / 文档 / 测试" : "local UI / docs / tests"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部执行" : "external execution"}</span>
            <strong>{state.localProductPolishRoadmap.canExecuteExternalCount}</strong>
            <small>can_execute_external=false</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "用户决策" : "user decisions"}</span>
            <strong>{state.localProductPolishRoadmap.remainingUserDecisionCount}</strong>
            <small>{locale === "zh-CN" ? "仍保持阻断" : "still blocked"}</small>
          </article>
        </div>
        <div className="handoff-readiness-boundary">
          {state.localProductPolishRoadmap.defaultBlockers.map((blocker) => (
            <span key={blocker}>{blocker}</span>
          ))}
        </div>
        <div className="handoff-readiness-card-grid">
          {state.localProductPolishRoadmap.roadmapItems.map((item) => (
            <article key={item.id} className="handoff-readiness-card">
              <div>
                <span>P{item.priority}</span>
                <strong>{locale === "zh-CN" ? item.titleZh : item.titleEn}</strong>
              </div>
              <p>{locale === "zh-CN" ? item.outcomeZh : item.outcomeEn}</p>
              <small>
                {item.allowedEffect} · can_execute_external={String(item.canExecuteExternal)}
              </small>
              <small>
                {locale === "zh-CN" ? "证据" : "evidence"}: {item.evidenceSources[0]}
              </small>
            </article>
          ))}
        </div>
        <div className="handoff-readiness-channels">
          {state.localProductPolishRoadmap.qualityGates.map((gate) => (
            <span key={gate}>{gate}</span>
          ))}
        </div>
      </section>

      <section
        id="decisions"
        className="panel-section user-decision-center-section"
        aria-labelledby="user-decision-center-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · External-action gate</p>
          <h2 id="user-decision-center-title">
            {locale === "zh-CN" ? "用户决策中心" : "User decision center"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "真实云同步、生态发布和生产级外部分发都不是 Codex 可以自动越过的步骤。这里把每个决策拆成你后续要确认的选择、仍被禁止的外部动作和正式激活路径。"
              : "Real cloud sync, ecosystem publishing and production external distribution are not steps Codex may cross autonomously. This center shows the choices you must later confirm, the external actions still forbidden and the formal activation path."}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "可立即执行" : "executable now"}</span>
            <strong>{state.userDecisionCenter.canExecuteNowCount}</strong>
            <small>can_execute_now=false</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "决策项" : "decisions"}</span>
            <strong>{state.userDecisionCenter.decisionCount}</strong>
            <small>requires_user_decision=true</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部阻断" : "external blockers"}</span>
            <strong>{state.userDecisionCenter.blockedExternalActionCount}</strong>
            <small>{state.userDecisionCenter.status}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "安全自主动作" : "safe autonomous actions"}</span>
            <strong>{state.userDecisionCenter.safeAutonomousActionCount}</strong>
            <small>{state.userDecisionCenter.safeAutonomousActions[0]?.id}</small>
          </article>
        </div>
        <div className="user-decision-center-boundary">
          {[
            locale === "zh-CN" ? "审批权 0" : "approval authority 0",
            locale === "zh-CN" ? "决策存储 0" : "decision storage 0",
            locale === "zh-CN" ? "Secret 收集 0" : "Secret collection 0",
            locale === "zh-CN" ? "云同步 0" : "cloud sync 0",
            locale === "zh-CN" ? "发布 0" : "publish 0",
            locale === "zh-CN" ? "分发 0" : "distribution 0",
            locale === "zh-CN" ? "付费 0" : "paid 0",
            "Runtime 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="user-decision-center-card-grid">
          {state.userDecisionCenter.decisions.map((decision) => (
            <article key={decision.id} className="user-decision-center-card">
              <div>
                <span>
                  {decision.category} · {decision.state}
                </span>
                <strong>{locale === "zh-CN" ? decision.titleZh : decision.titleEn}</strong>
              </div>
              <p>{locale === "zh-CN" ? decision.reasonZh : decision.reasonEn}</p>
              <small>
                {locale === "zh-CN" ? "待用户选择" : "choices"}:{" "}
                {decision.userChoicesRequired
                  .slice(0, 3)
                  .map((choice) => (locale === "zh-CN" ? choice.labelZh : choice.labelEn))
                  .join(" · ")}
              </small>
              <small>
                {locale === "zh-CN" ? "仍禁止" : "forbidden"}:{" "}
                {decision.forbiddenUntilComplete.slice(0, 4).join(" · ")}
              </small>
              <small>
                {locale === "zh-CN" ? "激活路径" : "activation path"}:{" "}
                {decision.activationPath.slice(-3).join(" → ")}
              </small>
            </article>
          ))}
        </div>
      </section>

      <section
        id="decision-readiness"
        className="panel-section user-decision-center-section decision-packs-readiness-section"
        aria-labelledby="decision-readiness-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Decision readiness</p>
          <h2 id="decision-readiness-title">
            {locale === "zh-CN"
              ? state.userDecisionPacksReadiness.titleZh
              : state.userDecisionPacksReadiness.titleEn}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? state.userDecisionPacksReadiness.bodyZh
              : state.userDecisionPacksReadiness.bodyEn}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article>
            <span>{locale === "zh-CN" ? "决策包" : "decision packs"}</span>
            <strong>{state.userDecisionPacksReadiness.decisionPackCount}</strong>
            <small>{state.userDecisionPacksReadiness.status}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "待填选择" : "missing choices"}</span>
            <strong>{state.userDecisionPacksReadiness.missingUserInputCount}</strong>
            <small>{state.userDecisionPacksReadiness.requiredUserInputCount} required</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "停止条件" : "stop conditions"}</span>
            <strong>{state.userDecisionPacksReadiness.stopConditionCount}</strong>
            <small>fail closed</small>
          </article>
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "可执行" : "executable now"}</span>
            <strong>{state.userDecisionPacksReadiness.canExecuteNowCount}</strong>
            <small>can_execute_now=false</small>
          </article>
        </div>
        <div className="user-decision-center-boundary">
          {state.userDecisionPacksReadiness.guardrails.map((guardrail) => (
            <span key={guardrail}>{guardrail}</span>
          ))}
        </div>
        <div className="user-decision-center-card-grid decision-pack-readiness-grid">
          {state.userDecisionPacksReadiness.readinessCards.map((card) => (
            <article key={card.id} className="user-decision-center-card">
              <div>
                <span>
                  {card.category} · {card.state}
                </span>
                <strong>{locale === "zh-CN" ? card.titleZh : card.titleEn}</strong>
              </div>
              <p>{locale === "zh-CN" ? card.whyNotNowZh : card.whyNotNowEn}</p>
              <small>
                {locale === "zh-CN" ? "第一安全步" : "first safe step"}:{" "}
                {locale === "zh-CN" ? card.firstSafeStepZh : card.firstSafeStepEn}
              </small>
              <small>
                {locale === "zh-CN" ? "待填" : "missing"}: {card.missingUserInputs.slice(0, 4).join(" · ")}
              </small>
              <small>
                {locale === "zh-CN" ? "禁止" : "no-go"}: {card.noGoActions.slice(0, 4).join(" · ")}
              </small>
              <small>
                can_start_now={String(card.canStartNow)} · can_execute_now=
                {String(card.canExecuteNow)}
              </small>
            </article>
          ))}
        </div>
        <div className="handoff-readiness-channels">
          {state.userDecisionPacksReadiness.decisionLadder.map((step) => (
            <span key={step.step}>
              {step.step}. {locale === "zh-CN" ? step.labelZh : step.labelEn} · {step.allowedEffect}
            </span>
          ))}
        </div>
      </section>

      <section
        id="activation"
        className="panel-section user-decision-center-section"
        aria-labelledby="user-decision-activation-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Activation pack</p>
          <h2 id="user-decision-activation-title">
            {locale === "zh-CN" ? "用户决策激活包" : "User decision activation pack"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "这不是执行按钮，而是下一轮启动说明书：当你明确选择某个外部动作时，AIOS 会从这里进入 Task Spec、Secret 路径确认、只读预检、Canary、回滚演练和独立审查。"
              : "These are not execution buttons. They are the next-run playbooks: once you explicitly choose an external action, AIOS moves from here into Task Spec, Secret-path confirmation, read-only preflight, canary, rollback drill and independent review."}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article>
            <span>{locale === "zh-CN" ? "激活包" : "activation packs"}</span>
            <strong>{state.userDecisionActivationPack.activationPackCount}</strong>
            <small>{state.userDecisionActivationPack.status}</small>
          </article>
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "可立即启动" : "can start now"}</span>
            <strong>{state.userDecisionActivationPack.canStartNowCount}</strong>
            <small>can_start_now=false</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "外部副作用" : "external effects"}</span>
            <strong>{state.userDecisionActivationPack.externalEffectCount}</strong>
            <small>{locale === "zh-CN" ? "仍为 0" : "still 0"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Secret 值" : "secret values"}</span>
            <strong>{state.userDecisionActivationPack.secretValuesEmbeddedCount}</strong>
            <small>{locale === "zh-CN" ? "不在网页存储" : "not stored here"}</small>
          </article>
        </div>
        <div className="user-decision-center-boundary">
          {[
            locale === "zh-CN" ? "审批权 0" : "approval authority 0",
            locale === "zh-CN" ? "决策存储 0" : "decision storage 0",
            locale === "zh-CN" ? "Secret 值 0" : "secret values 0",
            locale === "zh-CN" ? "云写入 0" : "cloud write 0",
            locale === "zh-CN" ? "发布 0" : "publish 0",
            locale === "zh-CN" ? "分发 0" : "distribution 0",
            locale === "zh-CN" ? "付费 0" : "paid 0",
            "Runtime 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="user-decision-center-card-grid">
          {state.userDecisionActivationPack.packs.map((pack) => (
            <article key={pack.id} className="user-decision-center-card">
              <div>
                <span>
                  {pack.category} · {pack.currentAllowedEffect}
                </span>
                <strong>{locale === "zh-CN" ? pack.titleZh : pack.titleEn}</strong>
              </div>
              <p>{locale === "zh-CN" ? pack.copyPromptZh : pack.copyPromptEn}</p>
              <small>
                {locale === "zh-CN" ? "必填选择" : "required inputs"}:{" "}
                {pack.requiredUserInputs.slice(0, 4).join(" · ")}
              </small>
              <small>
                {locale === "zh-CN" ? "停止条件" : "stop conditions"}:{" "}
                {pack.stopConditions.slice(0, 3).join(" · ")}
              </small>
              <small>
                {locale === "zh-CN" ? "最后三步" : "last three steps"}:{" "}
                {pack.activationSequence.slice(-3).join(" → ")}
              </small>
            </article>
          ))}
        </div>
      </section>

      <section
        id="prompts"
        className="panel-section user-decision-center-section"
        aria-labelledby="user-decision-prompts-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Read-only prompts</p>
          <h2 id="user-decision-prompts-title">
            {locale === "zh-CN" ? "决策提示面板" : "Decision prompt surface"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "三条启动提示以只读 textarea 呈现，方便你手动复制并填空。页面不写剪贴板、不提交表单、不保存用户选择、不收集密钥。"
              : "The three activation prompts are rendered as read-only textareas for manual copy-and-fill. The page does not write the clipboard, submit forms, store user choices or collect secrets."}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article>
            <span>{locale === "zh-CN" ? "提示文本" : "prompts"}</span>
            <strong>{state.userDecisionPromptSurface.promptSurfaceCount}</strong>
            <small>{state.userDecisionPromptSurface.status}</small>
          </article>
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "按钮允许" : "buttons allowed"}</span>
            <strong>{state.userDecisionPromptSurface.buttonCountAllowed}</strong>
            <small>{locale === "zh-CN" ? "无复制/执行按钮" : "no copy/execute buttons"}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "剪贴板写入" : "clipboard writes"}</span>
            <strong>{Number(state.userDecisionPromptSurface.clipboardWriteAllowed)}</strong>
            <small>clipboard_write_allowed=false</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Secret 值" : "secret values"}</span>
            <strong>{state.userDecisionPromptSurface.secretValuesEmbeddedCount}</strong>
            <small>secret_values_embedded_count=0</small>
          </article>
        </div>
        <div className="user-decision-center-boundary">
          {[
            locale === "zh-CN" ? "决策存储 0" : "decision storage 0",
            locale === "zh-CN" ? "Secret 收集 0" : "Secret collection 0",
            locale === "zh-CN" ? "表单提交 0" : "form submit 0",
            locale === "zh-CN" ? "外部写入 0" : "external write 0",
            locale === "zh-CN" ? "付费 0" : "paid 0",
            "Runtime 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="user-decision-center-card-grid">
          {state.userDecisionPromptSurface.prompts.map((prompt) => {
            const pack = state.userDecisionActivationPack.packs.find(
              (candidate) => candidate.id === prompt.sourcePackId,
            );
            return (
              <article key={prompt.id} className="user-decision-center-card">
                <div>
                  <span>
                    {prompt.id} · {prompt.sourcePackId} ·{" "}
                    {locale === "zh-CN" ? "只读 textarea" : "read-only textarea"}
                  </span>
                  <strong>{locale === "zh-CN" ? prompt.titleZh : prompt.titleEn}</strong>
                </div>
                <textarea
                  aria-label={`${locale === "zh-CN" ? prompt.titleZh : prompt.titleEn} · ${
                    locale === "zh-CN" ? "只读激活提示" : "read-only activation prompt"
                  }`}
                  className="activation-prompt-textarea"
                  readOnly
                  rows={7}
                  value={pack ? (locale === "zh-CN" ? pack.copyPromptZh : pack.copyPromptEn) : ""}
                />
                <small>
                  {prompt.sourceDecisionId} · stores_user_choice={String(prompt.storesUserChoice)}
                </small>
              </article>
            );
          })}
        </div>
      </section>

      <section
        id="safety-harness"
        className="panel-section user-decision-center-section"
        aria-labelledby="external-action-safety-harness-title"
      >
        <div className="section-heading">
          <p className="eyebrow">L5 · Fail-closed safety</p>
          <h2 id="external-action-safety-harness-title">
            {locale === "zh-CN" ? "外部动作安全锁" : "External action safety harness"}
          </h2>
          <p className="section-copy">
            {locale === "zh-CN"
              ? "真实云同步、生态发布与生产级外部分发都先被锁进本地安全锁：缺少用户选择、显式 go、Reviewer、Evidence、回滚或成本边界时，默认阻断。这里没有启动、登录、授权、发布、分发或写外部按钮。"
              : "Real cloud sync, ecosystem publishing and production external distribution are locked behind a local safety harness: missing user choices, explicit go, Reviewer, Evidence, rollback or cost boundary means blocked by default. There are no start, login, authorize, publish, distribute or external-write buttons here."}
          </p>
        </div>
        <div className="user-decision-center-dashboard">
          <article>
            <span>{locale === "zh-CN" ? "受控动作" : "controlled actions"}</span>
            <strong>{state.externalActionSafetyHarness.controlledActionCount}</strong>
            <small>{state.externalActionSafetyHarness.status}</small>
          </article>
          <article className="user-decision-center-decision-card">
            <span>{locale === "zh-CN" ? "可执行" : "executable now"}</span>
            <strong>{state.externalActionSafetyHarness.canExecuteNowCount}</strong>
            <small>{state.externalActionSafetyHarness.defaultResult}</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "前置条件" : "preconditions"}</span>
            <strong>{state.externalActionSafetyHarness.preconditionTotal}</strong>
            <small>all_required_before_go=true</small>
          </article>
          <article>
            <span>{locale === "zh-CN" ? "Secret 值" : "secret values"}</span>
            <strong>{state.externalActionSafetyHarness.secretValuesEmbeddedCount}</strong>
            <small>secret_values_embedded_count=0</small>
          </article>
        </div>
        <div className="user-decision-center-boundary">
          {[
            "fail_closed=true",
            "local_check_only=true",
            locale === "zh-CN" ? "审批权 0" : "approval authority 0",
            locale === "zh-CN" ? "决策存储 0" : "decision storage 0",
            locale === "zh-CN" ? "Secret 收集 0" : "Secret collection 0",
            locale === "zh-CN" ? "外部写入 0" : "external write 0",
            locale === "zh-CN" ? "付费 0" : "paid 0",
            "Runtime 0",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="user-decision-center-card-grid">
          {state.externalActionSafetyHarness.controlledActions.map((action) => (
            <article key={action.id} className="user-decision-center-card">
              <div>
                <span>
                  {action.id} · {locale === "zh-CN" ? "已锁定" : "locked"} · can_execute_now=
                  {String(action.canExecuteNow)}
                </span>
                <strong>{action.decisionId}</strong>
                <small>{action.templateId}</small>
              </div>
              <p>
                {locale === "zh-CN" ? "必过条件" : "required gates"}:{" "}
                {Array.from(new Set([...action.requiredPreconditions.slice(0, 3), "explicit_user_go"])).join(
                  " · ",
                )}
              </p>
              <small>
                {locale === "zh-CN" ? "未 go 禁止" : "forbidden before go"}:{" "}
                {action.forbiddenWithoutUserGo.slice(0, 4).join(" · ")}
              </small>
              <small>Evidence: {action.evidenceTarget}</small>
            </article>
          ))}
        </div>
      </section>

      <section id="sources" className="panel-section source-findings-section">
        <div className="section-heading">
          <p className="eyebrow">Evidence-backed · read-only</p>
          <h2>{t(locale, "sourceFindings")}</h2>
          <p className="section-copy">{t(locale, "sourceFindingsBody")}</p>
        </div>
        <div className="source-findings-grid">
          {state.sourceFindings.map((finding) => (
            <article key={finding.id} className="source-finding-card">
              <div className="source-finding-card__head">
                <span>{finding.source}</span>
                <strong>{locale === "zh-CN" ? finding.titleZh : finding.titleEn}</strong>
                <small>{finding.status}</small>
              </div>
              <p>{locale === "zh-CN" ? finding.noteZh : finding.noteEn}</p>
              <div className="source-finding-metrics">
                <Metric
                  label={t(locale, "sourceRequests")}
                  value={`${finding.requestsOk}/${finding.requestsTotal}`}
                />
                <Metric label={t(locale, "sourceBytes")} value={finding.bytesRead.toLocaleString()} />
                <Metric label={t(locale, "sourceDirectory")} value={String(finding.directoryEntries)} />
                <Metric label="License" value={finding.license} />
              </div>
              <div className="source-language-strip">
                {finding.languageMix.map((entry) => (
                  <span key={entry.language}>
                    {entry.language} {entry.sharePercent}%
                  </span>
                ))}
              </div>
              <div className="source-boundary-strip" role="group" aria-label={t(locale, "sourceBoundary")}>
                {(locale === "zh-CN" ? finding.boundaryTagsZh : finding.boundaryTagsEn).map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
              <div className="source-next-gate">
                <span>{t(locale, "sourceNextGate")}</span>
                <strong>{finding.nextGate}</strong>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="panel-section">
        <div className="section-heading">
          <p className="eyebrow">Gate</p>
          <h2>{t(locale, "longGateGuard")}</h2>
        </div>
        <div className="gate-grid">
          {state.longGates.map((gate) => (
            <article key={gate.id} className="gate-card">
              <span className={`status-pill ${gate.safeToWait ? "live" : "stale"}`}>{gate.status}</span>
              <h3>{locale === "zh-CN" ? gate.titleZh : gate.titleEn}</h3>
              <p>{locale === "zh-CN" ? gate.messageZh : gate.messageEn}</p>
              <ul>
                <li>activity age: {gate.latestActivityAgeMinutes}m</li>
                <li>terminal file: {gate.terminalFileRequired ? "required" : "missing"}</li>
                <li>stop condition: {gate.stopConditionRequired ? "required" : "missing"}</li>
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section id="features" className="feature-grid">
        {state.features.map((feature) => (
          <article key={feature.id} className="feature-card">
            <h2>{locale === "zh-CN" ? feature.titleZh : feature.titleEn}</h2>
            <p>{locale === "zh-CN" ? feature.bodyZh : feature.bodyEn}</p>
          </article>
        ))}
      </section>

      <section className="panel-section">
        <div className="section-heading">
          <p className="eyebrow">Safety</p>
          <h2>{t(locale, "riskControl")}</h2>
        </div>
        <div className="risk-grid">
          {state.risks.map((risk) => (
            <article key={risk.id} className="risk-card">
              <h3>{locale === "zh-CN" ? risk.titleZh : risk.titleEn}</h3>
              <p>{locale === "zh-CN" ? risk.mitigationZh : risk.mitigationEn}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="login" className="login-card">
        <div>
          <p className="eyebrow">Identity</p>
          <h2>{session.authenticated ? t(locale, "signedIn") : t(locale, "signedOut")}</h2>
          <p>{session.email ?? "Google OAuth · Cloudflare Pages Functions"}</p>
          {session.admin ? <span className="status-pill live">{t(locale, "admin")}</span> : null}
        </div>
        {session.authenticated ? (
          <button
            type="button"
            className="button secondary"
            onClick={async () => {
              await logout();
              setSession({ authenticated: false });
            }}
          >
            {t(locale, "logout")}
          </button>
        ) : (
          <a className="button primary" href="/api/auth/google/start">
            {t(locale, "googleLogin")}
          </a>
        )}
      </section>
    </main>
  );
}

function ControlGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="control-group">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
